import {
  Box,
  FormControl,
  MenuItem,
  ToggleButton as MuiToggleButton,
  ToggleButtonGroup as MuiToggleButtonGroup,
  Select,
  Switch,
  Typography,
  styled,
} from '@mui/material';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'react-i18next';
import { uiLanguage } from '~/lib/i18n';
import { CountField } from './count-field';
import { daysOfWeek, weekdayNames, type Day } from './lib/helpers';
import type { useScheduleOptions } from './lib/schedule-options';

type RepeatType = 'Daily' | 'Weekly';
type EndType = 'Never' | 'After' | 'On date';

const ToggleButtonGroup = styled(MuiToggleButtonGroup)(({ theme, disabled }) => ({
  '&.MuiToggleButtonGroup-root': {
    display: 'flex',
    gap: theme.spacing(1),
  },
  '& > .MuiToggleButton-root.Mui-disabled': {
    backgroundColor: disabled ? theme.palette.borders.main : theme.palette.primary.dark,
    color: disabled ? theme.palette.text.disabled : theme.palette.common.white,
  },
}));

const ToggleButton = styled(MuiToggleButton)(({ theme }) => ({
  borderRadius: '20px !important', // love this
  width: '40px',
  height: '40px',
  borderLeft: '1px solid #eee !important',
  padding: theme.spacing(1),
}));
export interface ScheduleFieldValues {
  scheduleOptions: ReturnType<typeof useScheduleOptions>;
}

export const ScheduleFields = ({ scheduleOptions }: ScheduleFieldValues) => {
  const { t } = useTranslation();
  const dayNames = weekdayNames(uiLanguage());
  const {
    dtstart,
    setRepeatType,
    repeatType,
    setWeekDays,
    weekDays,
    allDay,
    start,
    end,
    setAllDay,
    setEndType,
    endType,
    setCount,
    count,
    until,
  } = scheduleOptions;

  return (
    <>
      <label>
        <span className="label required">{t('components.schedule.fields.date_times')}</span>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            width: '100%',
          }}
        >
          <FormControl sx={{ mt: 1, width: '50%' }} variant="outlined">
            <DatePicker
              {...dtstart.props}
              slotProps={{
                textField: {
                  inputProps: { readOnly: true },
                },
              }}
            />
          </FormControl>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              width: '50%',
              opacity: allDay ? 0 : 1,
              mt: 1,
            }}
          >
            <FormControl sx={{ width: '50%' }} variant="outlined">
              <TimePicker {...start.props} disabled={allDay} />
            </FormControl>
            <Typography>{t('components.schedule.fields.to')}</Typography>
            <FormControl sx={{ width: '50%' }} variant="outlined">
              <TimePicker {...end.props} disabled={allDay} />
            </FormControl>
          </Box>
        </Box>
      </label>

      <label>
        <span className="label">{t('components.schedule.fields.all_day')}</span>
        <FormControl>
          <Switch checked={allDay} color="primary" onChange={() => setAllDay(!allDay)} />
        </FormControl>
      </label>
      <label>
        <span className="label">{t('components.schedule.fields.repeats')}</span>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 1,
            width: '100%',
          }}
        >
          <FormControl sx={{ width: '25%' }} variant="outlined">
            <Select
              // The wrapping label cannot name a select, whose control is a div.
              inputProps={{ 'aria-label': t('components.schedule.fields.repeats') }}
              margin="none"
              onChange={(event) => setRepeatType(event.target.value as RepeatType)}
              value={repeatType}
              sx={{ marginRight: '8px' }}
            >
              <MenuItem value="Never">{t('components.schedule.fields.repeat_never')}</MenuItem>
              <MenuItem value="Daily">{t('components.schedule.fields.repeat_daily')}</MenuItem>
              <MenuItem value="Weekly">{t('components.schedule.fields.repeat_weekly')}</MenuItem>
            </Select>
          </FormControl>

          {repeatType === 'Weekly' && (
            <ToggleButtonGroup
              aria-label={t('components.schedule.fields.days_of_week')}
              onChange={(_event, newValue: readonly Day[]) => {
                if (newValue.length) setWeekDays(newValue);
              }}
              value={weekDays}
            >
              {daysOfWeek.map((day, index) => (
                <ToggleButton key={day} value={day} aria-label={dayNames[index]}>
                  {t(`components.schedule.fields.days.${day.toLowerCase()}`)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}
        </Box>
      </label>
      {repeatType !== 'Never' && (
        <label>
          <span className="label">{t('components.schedule.fields.ends')}</span>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
              width: '100%',
            }}
          >
            <FormControl sx={{ width: '25%' }} variant="outlined">
              <Select
                inputProps={{ 'aria-label': t('components.schedule.fields.ends') }}
                margin="none"
                onChange={(event) => setEndType(event.target.value as EndType)}
                value={endType}
                sx={{ marginRight: '8px' }}
              >
                <MenuItem value="Never">{t('components.schedule.fields.end_never')}</MenuItem>
                <MenuItem value="After">{t('components.schedule.fields.end_after')}</MenuItem>
                <MenuItem value="On date">{t('components.schedule.fields.end_on')}</MenuItem>
              </Select>
            </FormControl>

            {endType === 'Never' ? null : endType === 'After' ? (
              <CountField
                set={setCount}
                unit={t('components.schedule.fields.occurrence')}
                value={count}
              />
            ) : (
              <FormControl sx={{ width: '75%' }} variant="outlined">
                <DatePicker
                  {...until.props}
                  slotProps={{
                    textField: {
                      inputProps: { readOnly: true },
                    },
                  }}
                />
              </FormControl>
            )}
          </Box>
        </label>
      )}
    </>
  );
};
